// vendor
import { defineComponent, ref, computed, PropType, watch, reactive, inject, ComputedRef } from "@vue/composition-api";
// project
import * as SessionAPI from "@/services/api/sessions";
import { useTranslate } from "@/plugins/translate";
import { generateGUID } from "@/utils/guid";
import { SessionContext } from "@/components.run/run-session";
// local
import RunFormItem from "../vue/run-form-item.vue";
import { default as AlertBox } from "../vue/alert-box.vue";
import { default as ErrorBox } from "../vue/items/error-box.vue";
import { FormData, DataChannel, FormItem } from "./types";


type DisplayItem = {
	key:string,
	section:string,
	config:FormItem<any>
};

type FormItemComponent = {
	isValid?:() => boolean,
	getChannels?:(prefix:string) => DataChannel<any>[]
};

export const RunForm = defineComponent({

	props:{
		data:{
			type:Object as PropType<FormData>,
			required:true
		},
		dataNamespace:{
			type:String,
			required:true
		},
		showBack:{
			type:Boolean,
			default:false
		},
		isLast:{
			type:Boolean,
			default:false
		}

	},

	components:{
		RunFormItem,
		AlertBox,
		ErrorBox,
	},
	setup(props, context){

		const getSessionContext = inject<() => SessionContext>("getSessionContext");

		if(!getSessionContext){
			throw Error("Run Form: Session context missing!")
		}

		const {
			isMobile,
		} = getSessionContext();

		const formItems = ref<(FormItemComponent)[]>([]);

		const buttonKeys = reactive({
			back:generateGUID(),
			next:generateGUID()
		});

		const containsErrors = ref(false);

		const loading = ref(false);
		const sectionIndex = ref(0);

		const canGoBack = computed(() => props.showBack || sectionIndex.value > 0)

		const cache = computed(() => context.root.$store.state.cache["storage"]);

		const isEmptyForm = computed(() => {
			return (props.data as any)["asset"] === null;
		});

		const onLast = computed(() => {
			if(isEmptyForm.value){ return props.isLast; }
			return sectionIndex.value + 1 === props.data.sectionOrder.length && props.isLast;
		});

		const sessionId = computed(() => context.root.$route.params["sessionId"]);


		const setProgressStep:any = inject("setProgressStep");

		const showProgressBar = inject<ComputedRef<boolean>>("showProgressBar");

		const refreshId = computed(() => {
			return currentSection.value ? currentSection.value.id : generateGUID();
		});

		const displayItems = computed(() => {
			if(!props.data){ return []; }
			const items:DisplayItem[] = [];
			props.data.sectionOrder.forEach(sectionId => {
				const s = props.data.sections.find(s => s.id === sectionId);
				if(!s){ return; }
				s.itemOrder.forEach(itemId => {
					const c = s.items.find(it => it.id === itemId);
					if(!c){ return; }
					items.push({
						key: `${props.dataNamespace}/${itemId}`,
						section: sectionId,
						config: c
					});
				});
			});
			return items;
		});

		const showItem = (item:FormItem<any>) => {
			if(!item.platforms){ return true; }
			if(!item.platforms.length){ return true; }
			const current = isMobile.value ? "mobile" : "desktop";
			return item.platforms.indexOf(current) >= 0;
		};

		const refreshProgress = () => {
			if(sectionIndex.value < 0){ return; }
			if(isEmptyForm.value){ return; }
			const s = props.data.sectionOrder[sectionIndex.value];
			if(!s){ return; }
			setProgressStep(`${props.dataNamespace}/${s}`);
		};

		const currentSection = computed(() => {
			if(!Boolean(props.data.sectionOrder) || sectionIndex.value < 0){ return null; }
			const id = props.data.sectionOrder[sectionIndex.value];
			return props.data.sections.find(s => s.id === id);
		});

		// cache page index
		watch(sectionIndex, i => {
			cache.value[`${props.dataNamespace}.index`] = i;
			SessionAPI.cacheSessionValue(sessionId.value, `${props.dataNamespace}.index`, i);
			refreshProgress();
		});

		const { translate } = useTranslate(context);

		const onItemTouched = (e:{ id:string }) => {
			// console.log(event);
			containsErrors.value = false;
		};

		const saveData = async(channels:DataChannel<any>[]) => {

			let success = true;

			const unsaved = channels.filter(ch => {
				const cached = cache.value[`${ch.key}.saved`];
				if(cached === undefined){ return true; }
				if(cached === ch.data){ return false; }
				return true;
			});

			if(unsaved.length === 0){ return true; }
	
			loading.value = true;
			let error = true;
			try {
				const result = await SessionAPI.saveDataChannels(sessionId.value, unsaved);
				for(var i = 0; i < result.length; i++){
					const success = result[i];
					const c = unsaved[i];
					if(success){
						cache.value[`${c.key}.saved`] = c.data;
						if(sessionId.value){
							SessionAPI.cacheSessionValue(sessionId.value, `${c.key}.saved`, c.data);
						}
					}
				}
				success = result.findIndex(s => !s) < 0;
			}
			catch(err){
				console.error(err);
				error = false;
				success = false;
			}
			loading.value = false;
			return success;
		};

		const regenButtonKeys = () => {
			buttonKeys.back = generateGUID();
			buttonKeys.next = generateGUID();
		};

		const next = async() => {

			let channels:DataChannel<any>[] = [];
			const items = formItems.value;
	
			for(var i = 0; i < items.length; i++){
				const fn = items[i].isValid;
				if(fn && !fn()){
					containsErrors.value = true;
					return;
				}
			}
	
			for(var i = 0; i < items.length; i++){
				const fn = items[i].getChannels;
				if(fn){
					fn(props.dataNamespace).forEach(c => channels.push(c));
				}
				// p.getChannels(props.dataNamespace).forEach(c => channels.push(c));
			}
	
			if(channels.length > 0){
				if(!(await saveData(channels))){ return; }
			}
	
			const ni = sectionIndex.value + 1;

			const solen = (!isEmptyForm.value ? props.data.sectionOrder : []).length;

			if(ni < solen){
				sectionIndex.value = ni;
			}
			else { context.emit("end"); }

			regenButtonKeys();

		};

		const back = () => {
			const i = sectionIndex.value - 1;
			if(i < 0){ context.emit("back"); }
			else { sectionIndex.value = i; }

			regenButtonKeys();
		};

		const init = () => {
			const sc = SessionAPI.getSessionCache(sessionId.value);
			if(sc){
				Object.keys(sc).forEach(k => context.root.$store.commit("cache/setValue", { k, v:sc[k] }));
			}
			const v = cache.value[`${props.dataNamespace}.index`];
			if(v){ sectionIndex.value = v; }

			refreshProgress();
		};

		const getButtonPhrase = (key:string) => {
			return translate(key).toUpperCase();
		};

		init();


		return {
			formItems,
			loading,
			sectionIndex,
			canGoBack,
			cache,
			onLast,
			displayItems,
			currentSection,
			buttonKeys,
			containsErrors,
			showProgressBar,
			refreshId,
			isEmptyForm,
			showItem,
			next,
			back,
			getButtonPhrase,
			onItemTouched,
		};

	}


});

